import { Badge, Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { ArrowClockwiseRegular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';

import { spacing } from '@/theme/tokens';

export interface FreshnessChipProps {
  asOfMs: number | null | undefined;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  className?: string;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const FRESH_MS = 5 * MINUTE_MS;
const AGING_MS = 30 * MINUTE_MS;

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
  },
  badge: {
    whiteSpace: 'nowrap',
  },
  fresh: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground2,
  },
  aging: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
  stale: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  updating: {
    color: tokens.colorNeutralForeground3,
  },
});

function pluralize(value: number, unit: string): string {
  return `${String(value)} ${unit}${value === 1 ? '' : 's'} ago`;
}

function formatFreshness(ageMs: number): string {
  if (ageMs < MINUTE_MS) {
    return 'Updated just now';
  }
  if (ageMs < HOUR_MS) {
    return `Updated ${pluralize(Math.max(1, Math.floor(ageMs / MINUTE_MS)), 'minute')}`;
  }
  if (ageMs < DAY_MS) {
    return `Updated ${pluralize(Math.max(1, Math.floor(ageMs / HOUR_MS)), 'hour')}`;
  }

  return `Updated ${pluralize(Math.max(1, Math.floor(ageMs / DAY_MS)), 'day')}`;
}

function freshnessToneClass(ageMs: number, classes: ReturnType<typeof useStyles>): string {
  if (ageMs <= FRESH_MS) {
    return classes.fresh;
  }
  if (ageMs <= AGING_MS) {
    return classes.aging;
  }
  return classes.stale;
}

export function FreshnessChip({ asOfMs, onRefresh, isRefreshing = false, className }: FreshnessChipProps) {
  const classes = useStyles();
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (asOfMs == null || !Number.isFinite(asOfMs)) {
      return undefined;
    }

    let timerId: number;

    // Schedule the next tick to fire exactly at the next age-unit boundary
    // (minute → hour → day) so labels change the moment they should, not up
    // to a full interval later. `Date.now()` is safe here — this runs in the
    // effect body and in async callbacks, never in the render function.
    function scheduleNext(): void {
      const ageMs = Date.now() - (asOfMs ?? 0);
      const nextMs =
        ageMs < HOUR_MS
          ? MINUTE_MS - (ageMs % MINUTE_MS)
          : HOUR_MS - (ageMs % HOUR_MS);
      timerId = window.setTimeout(() => {
        setNowMs(Date.now());
        scheduleNext();
      }, Math.max(nextMs, 1_000));
    }

    // Sync the clock immediately whenever asOfMs changes (e.g. after a refresh)
    // so the age label is correct from the very first render after new data lands.
    // Using setTimeout 0 keeps setState out of the synchronous effect body.
    timerId = window.setTimeout(() => {
      setNowMs(Date.now());
      scheduleNext();
    }, 0);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [asOfMs]);

  if (asOfMs == null || !Number.isFinite(asOfMs)) {
    return null;
  }

  const ageMs = Math.max(0, Math.max(nowMs, asOfMs) - asOfMs);

  return (
    <div className={mergeClasses(classes.root, className)}>
      <Badge appearance="filled" className={mergeClasses(classes.badge, freshnessToneClass(ageMs, classes))}>
        {formatFreshness(ageMs)}
      </Badge>
      {isRefreshing ? (
        <Badge
          appearance="outline"
          className={mergeClasses(classes.badge, classes.updating)}
          role="status"
          aria-label="Updating data"
          aria-live="polite"
          aria-atomic="true"
        >
          Updating data
        </Badge>
      ) : null}
      {onRefresh ? (
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowClockwiseRegular />}
          onClick={onRefresh}
          disabled={isRefreshing}
          aria-label="Refresh data"
        />
      ) : null}
    </div>
  );
}
